// The label scanner's "do the models agree?" comparison engine, in plain
// functions with no Angular, no DOM and no injected services.
//
// In 'all' mode the server asks every configured AI model to read the same
// label photos and returns one result per model. These functions decide which
// of those results say the same thing: results that agree are collapsed into
// one ResultGroup, and a single group means the models were unanimous (the page
// then goes straight to the food editor) while more than one raises the
// side-by-side comparison stepper.
//
// It lives beside the page rather than inside it so the agreement rule can be
// tested with plain objects instead of by driving the scanner against several
// real AI providers. Deferred finding df-d5e94b861ed7.

import { Nutrients } from '@cf-shared/data-model/food';
import { ScanLabelModelResult } from '@cf-shared/scan-label-interface';
import { SCAN_MODELS } from '@cf-shared/scan-models';

export interface ResultGroup {
  models: string[];         // model keys that agree
  displayNames: string[];   // display names for UI
  nutrients: Nutrients;
  measures?: any[];
  referenceWeight_g?: number;
  labelStyle?: 'US' | 'EU';
  confidences: { model: string, confidence: number }[];  // per-model overall confidence
  nutrientConfidences?: { [key: string]: number }; // best per-nutrient confidence across models in group
  jsonRepaired?: boolean;    // true if any model in the group had its JSON repaired
  foodName?: string;
  brandName?: string;
  ingredients?: string;
  description?: string;
  representativeImageIndex?: number;
  cropRect?: { x: number; y: number; w: number; h: number };
  barcode?: string;
}

/** One nutrient of one group, with how many models across all groups read it the same way. */
export interface NutrientAgreement {
  key: string;
  value: number;
  agreeCount: number;
  confidence: number;
}

/**
 * The one rule that decides whether two readings of the same nutrient count as
 * the same number. Both the grouping of model results and the per-nutrient
 * agreement count shown in the comparison UI go through this, so a change to
 * the tolerance changes both together.
 */
export function nutrientValuesAgree(a: number, b: number): boolean {
  // if both are zero there is nothing to compare
  if (a === 0 && b === 0) return true;

  // absolute difference check: < 0.1g
  const absDiff = Math.abs(a - b);
  if (absDiff < 0.1) return true;

  // relative difference check: < 1%
  const maxVal = Math.max(Math.abs(a), Math.abs(b));
  return maxVal > 0 && (absDiff / maxVal) < 0.01;
}

/** True when every nutrient present in either reading agrees under nutrientValuesAgree. */
export function nutrientsAreEqual(a: Nutrients, b: Nutrients): boolean {
  const allKeys = new Set([...Object.keys(a), ...Object.keys(b)]);
  for (const key of allKeys) {
    // a nutrient missing from one side is read as zero, the same way the UI shows it
    if (!nutrientValuesAgree(a[key] || 0, b[key] || 0)) return false;
  }
  return true;
}

/**
 * Collapse the models that read the label the same way into one group each,
 * most-agreement-first. Results only join a group when their nutrients agree
 * AND their label style matches — a US and an EU label are different readings
 * even when the numbers happen to line up.
 */
export function groupResultsBySimilarity(results: { key: string, result: ScanLabelModelResult }[]): ResultGroup[] {
  const groups: ResultGroup[] = [];

  for (const { key, result } of results) {
    const modelDef = SCAN_MODELS.find(m => m.key === key);
    const displayName = modelDef?.displayName || key;

    // try to find an existing group with matching nutrients AND labelStyle
    let matched = false;
    for (const group of groups) {
      if (group.labelStyle === result.labelStyle && nutrientsAreEqual(group.nutrients, result.nutrients)) {
        group.models.push(key);
        group.displayNames.push(displayName);
        group.confidences.push({ model: displayName, confidence: result.confidence });
        if (result.jsonRepaired) group.jsonRepaired = true;
        mergeNutrientConfidences(group, result.nutrientConfidences);
        matched = true;
        break;
      }
    }

    if (!matched) {
      groups.push({
        models: [key],
        displayNames: [displayName],
        nutrients: result.nutrients,
        measures: result.measures,
        referenceWeight_g: result.referenceWeight_g,
        labelStyle: result.labelStyle,
        confidences: [{ model: displayName, confidence: result.confidence }],
        nutrientConfidences: result.nutrientConfidences ? { ...result.nutrientConfidences } : undefined,
        jsonRepaired: result.jsonRepaired || undefined,
        foodName: result.foodName,
        brandName: result.brandName,
        ingredients: result.ingredients,
        description: result.description,
        representativeImageIndex: result.representativeImageIndex,
        cropRect: result.cropRect,
        barcode: result.barcode,
      });
    }
  }

  // sort groups by number of agreeing models (most agreement first)
  groups.sort((a, b) => b.models.length - a.models.length);
  return groups;
}

function mergeNutrientConfidences(group: ResultGroup, incoming?: { [key: string]: number }) {
  if (!incoming) return;
  if (!group.nutrientConfidences) {
    group.nutrientConfidences = { ...incoming };
    return;
  }
  // take the max confidence per nutrient
  for (const [key, val] of Object.entries(incoming)) {
    group.nutrientConfidences[key] = Math.max(group.nutrientConfidences[key] || 0, val);
  }
}

/**
 * The nutrients of one group, in display order, each with how many models in
 * total read it the same way. `nutrients` is the object of the group being
 * shown — the group it belongs to is found by identity within `groups`, so the
 * caller passes the very object it took out of that group.
 */
export function nutrientAgreements(groups: ResultGroup[], nutrients: Nutrients): NutrientAgreement[] {
  // find the current group to know how many models are in it
  const currentGroup = groups.find(g => g.nutrients === nutrients);
  const currentGroupModelCount = currentGroup?.models.length || 1;
  const nutrientConf = currentGroup?.nutrientConfidences;

  return Object.entries(nutrients)
    .filter(([, v]) => v > 0)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => {
      // count how many models across ALL groups agree on this nutrient value
      let agreeCount = currentGroupModelCount;
      for (const group of groups) {
        if (group.nutrients === nutrients) continue;
        if (nutrientValuesAgree(value, group.nutrients[key] || 0)) {
          agreeCount += group.models.length;
        }
      }
      return { key, value, agreeCount, confidence: nutrientConf?.[key] };
    });
}
